import React, {useEffect, useState} from 'react';
import LayoutLockSwitch from '../../../LayoutLockSwitch/LayoutLockSwitch';
import AddPrintsButton from '../../../RetroPrints/AddPrintsButton';
import UndoRedoButtons from '../../../UndoRedoButtons/UndoRedoButtons';
import PreviewViewModeSelector from '../../../ViewModeSelector/PreviewViewModeSelector';
import ViewModeSelector from '../../../ViewModeSelector/ViewModeSelector';
import ZoomControl from '../../../ZoomControl/ZoomControl';
import {ProductLayout} from '@taopix/taopix-core/dist/types/types';
import classNames from 'classnames';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import GridSizeZoomButtons from '../../../GridSizeSelector/GridSizeZoomButtons';
import {Theme, ThemeName} from '@taopix/taopix-design-system';
import {useExperience} from '../../../Experience/ExperienceProvider';
import useViewMode from '../../../../Hooks/useViewMode';
import {CollectionType, ViewMode} from '@taopix/taopix-core';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import KeyboardTips from '../../../KeyboardTips/KeyboardTips';
import useKeyboardMode from '../../../../Hooks/useKeyboardMode';

type WorkareaToolbarPropTypes = {
  layout: ProductLayout;
};

const WorkareaToolbar = ({layout}: WorkareaToolbarPropTypes) => {
  const ui = useUiState();
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  const [showUndoRedoButtons, setShowUndoRedoButtons] = useState<boolean>(false);
  const [showViewModeSelector, setShowViewModeSelector] = useState<boolean>(false);
  const keyboardMode = useKeyboardMode();

  const viewerToolbarClasses = classNames(
    'absolute',
    'top-0',
    'left-0',
    'px-sm',
    'flex',
    'z-20',
    'w-full',
    'justify-center',
    'lg:h-[50rem]',
    'sm:h-[60rem]',
    'transition-transform',
    // Position the toolbar correctly for focus mode
    // delayed slightly to avoid drawing issues caused by multiple translations happening simultaneously
    'focusMode:delay-[300ms]',
    'focusMode:translate-y-[var(--toolbarOffset)]',
    // Allow pointer events to be picked up by the canvasses:
    'pointer-events-none'
  );

  const leftSideClasses = classNames(
    'absolute',
    'left-sm',
    'flex',
    'flex-col',
    'items-start',
    'space-y-md',
    'mt-sm',
    'pointer-events-auto'
  );

  const centerClasses = classNames(
    'absolute',
    'flex',
    'mt-sm',
    'space-x-xs',
    'pointer-events-auto'
  );

  const rightSideClasses = classNames(
    'absolute',
    'right-sm',
    'flex',
    'mt-sm',
    'space-x-xs',
    'pointer-events-auto'
  );

  const buttonBarClasses = classNames('flex', 'shadow-md', 'overflow-hidden');

  const viewModes = [
    experience.EDITOR.workArea.editingViewModes.list,
    experience.EDITOR.workArea.editingViewModes.grid,
    experience.EDITOR.workArea.editingViewModes.page,
    experience.EDITOR.workArea.previewMode.includePreviewOption,
  ].filter(Boolean).length;

  useEffect(() => {
    // Update the visibility of the undo/redo and view mode buttons when the screen size changes
    setShowUndoRedoButtons(
      ui.state.isSmallScreen
        ? experience.EDITOR.workArea.general.undoRedoButtons.undoRedoSmall
        : experience.EDITOR.workArea.general.undoRedoButtons.undoRedoLarge
    );

    setShowViewModeSelector(
      ui.state.isSmallScreen
        ? experience.EDITOR.workArea.general.viewModeSelector.viewModeSelectorSmall
        : experience.EDITOR.workArea.general.viewModeSelector.viewModeSelectorLarge
    );
  }, [ui.state.isSmallScreen]);

  return (
    <div id={'viewerToolbar'} className={viewerToolbarClasses}>
      <div id="leftSideButtons" className={leftSideClasses}>
        {showUndoRedoButtons &&
          viewMode !== ViewMode.Preview &&
          CollectionType.Calendar !== layout.type && (
            // TO DO: Add logic to display only when the under/redo buttons are enabled in the experience for the CURRENT screen size:
            <Theme name={ThemeName.Popouts} className={buttonBarClasses}>
              <UndoRedoButtons
                buttonProps={{
                  size: ui.state.isSmallScreen && !ui.state.focusMode ? 'medium' : 'small',
                }}
              />
            </Theme>
          )}
        {!ui.state.isSmallScreen && keyboardMode && <KeyboardTips />}
      </div>

      <div id="centreButtons" className={centerClasses}>
        {!ui.state.isSmallScreen && (
          <>
            {showViewModeSelector && (
              <Theme name={ThemeName.Popouts} className={buttonBarClasses}>
                <ViewModeSelector
                  buttonProps={{buttonStyle: 'standard', size: 'small'} as ButtonProps}
                  showAsDropDown={'auto'}
                />
              </Theme>
            )}

            {!!layout.retroprints && ViewMode.Preview !== viewMode && (
              <Theme name={ThemeName.Popouts} className={buttonBarClasses}>
                <AddPrintsButton />
              </Theme>
            )}
          </>
        )}
      </div>

      <div id="rightSideButtons" className={rightSideClasses}>
        {ui.state.isSmallScreen && !ui.state.focusMode && (
          <>
            {!!layout.retroprints && (
              <Theme name={ThemeName.Popouts} className={buttonBarClasses}>
                <AddPrintsButton />
              </Theme>
            )}

            <LayoutLockSwitch />

            {showViewModeSelector && !layout.retroprints && viewModes > 1 && (
              <Theme name={ThemeName.Popouts} className={buttonBarClasses}>
                <ViewModeSelector showAsDropDown={ui.state.isSmallScreen} />
              </Theme>
            )}
          </>
        )}

        <GridSizeZoomButtons />

        {!layout.retroprints && (
          <>
            <ZoomControl />
            <PreviewViewModeSelector />
          </>
        )}
      </div>
    </div>
  );
};

export default WorkareaToolbar;
